import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import HexLogo from "./components/HexLogo.tsx";
import { api, type AuthClaims } from "./lib/api.ts";
import { socketService } from "./lib/socket.ts";
import Admin from "./pages/Admin.tsx";
import AuditLog from "./pages/AuditLog.tsx";
import Clipboard from "./pages/Clipboard.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import Files from "./pages/Files.tsx";
import Marketplace from "./pages/Marketplace.tsx";
import MessageQueue from "./pages/MessageQueue.tsx";
import Scheduler from "./pages/Scheduler.tsx";
import Scripts from "./pages/Scripts.tsx";
import Settings from "./pages/Settings.tsx";
import Variables from "./pages/Variables.tsx";
import Webhooks from "./pages/Webhooks.tsx";
import Workspaces from "./pages/Workspaces.tsx";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

export default function App() {
  const [syncKey, setSyncKey] = useState<string>(() => localStorage.getItem("solar_key") ?? "");
  const [authAttempt, setAuthAttempt] = useState(0);
  const [connected, setConnected] = useState(false);
  const [claims, setClaims] = useState<AuthClaims | null>(null);
  const [authState, setAuthState] = useState<"idle" | "checking" | "valid" | "invalid">(
    syncKey ? "checking" : "idle"
  );
  const [bundleBusy, setBundleBusy] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  function logout() {
    localStorage.removeItem("solar_key");
    socketService.disconnect();
    setConnected(false);
    setClaims(null);
    setAuthState("idle");
    setSyncKey("");
    setMobileNavOpen(false);
  }

  useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", mobileNavOpen);
    return () => document.body.classList.remove("mobile-nav-open");
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!syncKey) {
      setAuthState("idle");
      setClaims(null);
      return;
    }

    setAuthState("checking");
    if (import.meta.env.DEV) {
      console.info("[auth] validating sync key", { attempt: authAttempt, hasKey: Boolean(syncKey) });
    }
    api.auth
      .validate(syncKey)
      .then((res) => {
        if (res.valid) {
          setClaims(res.claims);
          setAuthState("valid");
          return;
        }
        setClaims(null);
        setAuthState("invalid");
      })
      .catch(() => {
        setClaims(null);
        setAuthState("invalid");
      });

    socketService.connect(syncKey);
    const unsub = socketService.onStatus((s) => setConnected(s === "connected"));
    return () => {
      unsub();
      socketService.disconnect();
    };
  }, [syncKey, authAttempt]);

  if (!syncKey || authState === "invalid") {
    return (
      <KeyEntry
        invalid={authState === "invalid"}
        onKey={(k) => {
          localStorage.setItem("solar_key", k);
          setSyncKey(k);
          setAuthAttempt((n) => n + 1);
          setAuthState("checking");
        }}
      />
    );
  }

  if (authState === "checking") {
    return (
      <div className="key-entry">
        <div className="key-entry__card">
          <div className="key-entry__logo">
            <HexLogo size={56} pulse />
          </div>
          <h1>Solar Agent</h1>
          <p>Validating access key...</p>
        </div>
      </div>
    );
  }

  const isAdmin = claims?.role === "admin" || claims?.plan === "admin" || claims?.features?.includes("admin");

  async function downloadPrepackagedBundle() {
    if (bundleBusy) return;
    setBundleBusy(true);
    try {
      const bundleBlob = await api.downloads.bundle(syncKey);
      const url = URL.createObjectURL(bundleBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "solar-agent.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to create prepackaged bundle", err);
      window.alert("Failed to create prepackaged bundle. Check console/logs.");
    } finally {
      setBundleBusy(false);
    }
  }

  return (
    <div className="app-layout">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={() => setMobileNavOpen((v) => !v)}
        aria-label="Toggle navigation"
      >
        <HexLogo size={16} gleam />
        <span className="mobile-nav-toggle__label">Solar Agent</span>
        <span className="mobile-nav-toggle__state">{mobileNavOpen ? "Close" : "Menu"}</span>
      </button>
      {mobileNavOpen && <button type="button" className="mobile-nav-scrim" onClick={() => setMobileNavOpen(false)} aria-label="Close menu" />}
      <nav className={`sidebar ${mobileNavOpen ? "sidebar--open" : ""}`}>
        <button type="button" className="sidebar-mobile-close" onClick={() => setMobileNavOpen(false)}>
          Close
        </button>
        <div className="sidebar-brand">
          <HexLogo size={22} gleam />
          <span className="sidebar-brand__text">Solar Agent</span>
        </div>
        <NavLink to="/" end className={navCls} onClick={() => setMobileNavOpen(false)}>
          Dashboard
        </NavLink>
        <NavLink to="/clipboard" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Clipboard
        </NavLink>
        <NavLink to="/files" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Files
        </NavLink>
        <NavLink to="/plugins" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Plugins
        </NavLink>
        <NavLink to="/workspaces" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Workspaces
        </NavLink>
        <NavLink to="/queue" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Queue
        </NavLink>
        <NavLink to="/scheduler" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Scheduler
        </NavLink>
        <NavLink to="/webhooks" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Webhooks
        </NavLink>
        <NavLink to="/vars" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Variables
        </NavLink>
        <NavLink to="/audit" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Audit Log
        </NavLink>
        <NavLink to="/marketplace" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Marketplace
        </NavLink>
        <NavLink to="/settings" className={navCls} onClick={() => setMobileNavOpen(false)}>
          Settings
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin" className={navCls} onClick={() => setMobileNavOpen(false)}>
            Admin
          </NavLink>
        )}
        <button type="button" className="download-link" onClick={downloadPrepackagedBundle}>
          <span className="download-link__icon">⬚</span>
          {bundleBusy ? "Building Bundle..." : "Download Prepackaged Bundle"}
        </button>
        <button type="button" className="logout-link" onClick={logout}>
          Log Out
        </button>
        <div className="sidebar-status">
          <span
            className={`status-dot ${connected ? "status-dot--online" : "status-dot--offline"}`}
          />
          {connected ? "Online" : "Offline"}
        </div>
      </nav>
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard syncKey={syncKey} />} />
          <Route path="/clipboard" element={<Clipboard syncKey={syncKey} />} />
          <Route path="/files" element={<Files syncKey={syncKey} />} />
          <Route path="/plugins" element={<Scripts syncKey={syncKey} />} />
          <Route path="/workspaces" element={<Workspaces syncKey={syncKey} />} />
          <Route path="/queue" element={<MessageQueue syncKey={syncKey} />} />
          <Route path="/scheduler" element={<Scheduler syncKey={syncKey} />} />
          <Route path="/webhooks" element={<Webhooks syncKey={syncKey} />} />
          <Route path="/vars" element={<Variables syncKey={syncKey} />} />
          <Route path="/audit" element={<AuditLog syncKey={syncKey} />} />
          <Route path="/marketplace" element={<Marketplace syncKey={syncKey} isAdmin={isAdmin} />} />
          <Route path="/scripts" element={<Navigate to="/plugins" replace />} />
          <Route
            path="/settings"
            element={<Settings syncKey={syncKey} />}
          />
          {isAdmin && <Route path="/admin" element={<Admin syncKey={syncKey} />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function navCls({ isActive }: { isActive: boolean }) {
  return `nav-link ${isActive ? "nav-link--active" : ""}`;
}

function KeyEntry({ onKey, invalid }: { onKey: (k: string) => void; invalid?: boolean }) {
  const devKey = import.meta.env.DEV ? (import.meta.env.VITE_DEV_SYNC_KEY as string | undefined) : undefined;
  const [val, setVal] = useState(devKey ?? "");

  useEffect(() => {
    if (devKey && !invalid) onKey(devKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="key-entry">
      <div className="key-entry__card">
        <div className="key-entry__logo">
          <HexLogo size={56} pulse />
        </div>
        <h1>Solar Agent</h1>
        <p>Access is restricted. A valid sync key is required to continue.</p>
        {invalid && (
          <p style={{ color: "#fca5a5", marginTop: -6, marginBottom: 2 }}>
            Access denied.
          </p>
        )}
        <input
          type="password"
          placeholder="Paste your sync key…"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && val.trim() && onKey(val.trim())}
        />
        <button type="button" onClick={() => val.trim() && onKey(val.trim())}>
          Connect
        </button>
        <div className="key-entry__divider" />
        <div className="key-entry__download">
          <p>This environment is invite-only.</p>
        </div>
      </div>
    </div>
  );
}
